import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaShoppingCart, FaSearch, FaTimes, FaUser } from 'react-icons/fa';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getCartItemCount } from '../utils/cartUtils';
import { getProductDisplayInfo } from '../utils/productUtils';
import { products } from '../data/productsData';
import MiniCartDrawer from './MiniCartDrawer';
import './Header.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef(null);
  const searchContainerDesktopRef = useRef(null);
  const searchContainerMobileRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { customer } = useCustomerAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateCartCount = () => {
      setCartItemCount(getCartItemCount());
    };

    updateCartCount();
    window.addEventListener('cartUpdated', updateCartCount);
    window.addEventListener('storage', updateCartCount);

    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
      window.removeEventListener('storage', updateCartCount);
    };
  }, []);

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const query = searchQuery.toLowerCase().trim();
      const filtered = products.filter(product => {
        const nameMatch = product.name.toLowerCase().includes(query);
        const descMatch = product.description.toLowerCase().includes(query);
        const shortDescMatch = product.shortDescription.toLowerCase().includes(query);
        const slugMatch = product.slug.toLowerCase().includes(query);
        return nameMatch || descMatch || shortDescMatch || slugMatch;
      });
      setSearchResults(filtered.slice(0, 5)); // Limit to 5 results
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const desktopEl = searchContainerDesktopRef.current;
      const mobileEl = searchContainerMobileRef.current;

      if (desktopEl && desktopEl.contains(event.target)) return;
      if (mobileEl && mobileEl.contains(event.target)) return;

      setIsSearchFocused(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setIsSearchFocused(true);
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchFocused(false);
    if (searchRef.current) {
      searchRef.current.focus();
    }
  };

  const handleProductClick = (product) => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchFocused(false);
    setIsMobileMenuOpen(false);
    navigate(`/product/${product.slug}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      handleProductClick(searchResults[0]);
    } else if (searchQuery.trim().length > 0) {
      // Navigate to products section if no results but query exists
      if (location.pathname !== '/') {
        navigate('/#products');
      } else {
        scrollToSection('products');
      }
      setSearchQuery('');
      setIsSearchFocused(false);
    }
  };

  const scrollToSection = (sectionId) => {
    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const handleHomeClick = (e) => {
    if (location.pathname !== '/') {
      e.preventDefault();
      window.location.href = '/';
    } else {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" onClick={handleHomeClick}>
            <motion.div
              className="logo-container"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img src="/Handmade haven.png" alt="Handmade Haven Logo" className="logo-image" loading="lazy" />
              <div className="logo-text">
                <p className="logo-text-main">Handmade Haven</p>
                <p className="logo-text-sub">HANDCRAFTED LOVE</p>
              </div>
            </motion.div>
          </Link>

          <div className="brand-name-mobile">
            <h2>Handmade Haven</h2>
            <p>HANDCRAFTED LOVE</p>
          </div>

          <ul className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
            <li><a href="/" onClick={handleHomeClick} className="nav-link">Home</a></li>
            <li><a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="nav-link">About</a></li>
            <li><a href="#products" onClick={(e) => { e.preventDefault(); scrollToSection('products'); }} className="nav-link">Products</a></li>
            <li><a href="#gallery" onClick={(e) => { e.preventDefault(); scrollToSection('gallery'); }} className="nav-link">Gallery</a></li>
            <li><a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="nav-link">Contact</a></li>
            <li className="mobile-only-nav-item">
              <Link to={customer ? "/account" : "/login"} className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                {customer ? "My Account" : "Login"}
              </Link>
            </li>
            <li className="cart-link-mobile-item">
              <Link to="/cart" className="nav-link cart-link-mobile" onClick={() => setIsMobileMenuOpen(false)}>
                <FaShoppingCart />
                Cart {cartItemCount > 0 && <span className="cart-badge">{cartItemCount}</span>}
              </Link>
            </li>
            <li className="mobile-order-btn">
              <motion.a
                href="#contact"
                onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
                className="btn-order"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Order Now
              </motion.a>
            </li>
          </ul>

          <div className="search-container-desktop" ref={searchContainerDesktopRef}>
            <form onSubmit={handleSearchSubmit} className="search-form-desktop">
              <div className="search-input-wrapper-desktop">
                <FaSearch className="search-icon-desktop" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  ref={searchRef}
                  className="search-input-desktop"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleSearchClear}
                    className="search-clear-desktop"
                    aria-label="Clear search"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </form>
            <AnimatePresence>
              {isSearchFocused && searchResults.length > 0 && (
                <motion.div
                  className="search-results-desktop"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {searchResults.map((product) => (
                    <div
                      key={product.id}
                      className="search-result-item-desktop"
                      onClick={() => handleProductClick(product)}
                    >
                      <img src={getProductDisplayInfo(product).displayImage} alt={product.name} className="search-result-image-desktop" />
                      <div className="search-result-info-desktop">
                        <h4>{product.name}</h4>
                        <p className="search-result-price-desktop">
                          {product.orderType === 'whatsapp' ? 'Contact for Pricing' : `₹ ${getProductDisplayInfo(product).displayPrice}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="header-actions">
            <motion.div
              className="icon-container"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Link
                to={customer ? "/account" : "/login"}
                className="icon-link"
                aria-label={customer ? "My Account" : "Login"}
              >
                <FaUser />
              </Link>
            </motion.div>

            <motion.div
              className="cart-icon-container"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <button
                className="cart-link"
                onClick={() => setIsDrawerOpen(true)}
                aria-label="Open shopping cart"
              >
                <FaShoppingCart />
                {cartItemCount > 0 && (
                  <motion.span
                    className="cart-badge"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500 }}
                  >
                    {cartItemCount}
                  </motion.span>
                )}
              </button>
            </motion.div>
            <motion.a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
              className="btn-order btn-order-desktop"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              Order Now
            </motion.a>
          </div>

          <div
            className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </nav>
      {/* Mobile search bar - always visible below header on small screens */}
      <div className={`mobile-search-wrapper ${isMobileMenuOpen ? 'menu-open' : ''}`}>
        <div className="search-container-mobile" ref={searchContainerMobileRef}>
          <form onSubmit={handleSearchSubmit} className="search-form-mobile">
            <div className="search-input-wrapper-mobile">
              <FaSearch className="search-icon-mobile" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                ref={searchRef}
                className="search-input-mobile"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleSearchClear}
                  className="search-clear-mobile"
                  aria-label="Clear search"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </form>
          <AnimatePresence>
            {isSearchFocused && searchResults.length > 0 && (
              <motion.div
                className="search-results-mobile"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {searchResults.map((product) => (
                  <div
                    key={product.id}
                    className="search-result-item-mobile"
                    onClick={() => handleProductClick(product)}
                  >
                    <img src={getProductDisplayInfo(product).displayImage} alt={product.name} className="search-result-image-mobile" />
                    <div className="search-result-info-mobile">
                      <h4>{product.name}</h4>
                      <p>{product.orderType === 'whatsapp' ? 'Contact for Pricing' : `₹ ${getProductDisplayInfo(product).displayPrice}`}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <MiniCartDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
    </header >
  );
};

export default Header;

